import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Reveal } from '../components/Reveal';
import { Ornament } from '../components/Ornament';
import { Interlude } from '../components/Interlude';
import { Mark } from '../components/Mark';
import { useContent } from '../content/ContentContext';
import { useAuth } from '../auth/AuthContext';
import { EditableText } from '../edit/Editable';
import { EditableImage } from '../edit/EditableImage';
import { PerformanceEditor } from '../edit/PerformanceEditor';
import {
  CONTACT_EMAIL,
  CONTACT_MAILTO,
  CONTACT_FORM_READY,
  CONTACT_FORM_ENDPOINT,
} from '../siteConfig';

export function Home() {
  return (
    <main>
      <Hero />
      <About />
      <Ornament />
      <Mentors />
      <Ornament />
      <Teaching />
      <Interlude />
      <Performance />
      <Ornament />
      <Listening />
      <Ornament />
      <Lessons />
      <Contact />
      <SettingsEditor />
    </main>
  );
}

// Editable intro line for the Contact section (kept separate so Contact keeps
// its own form state).
function ContactLede() {
  const { content } = useContent();
  return <EditableText as="p" className="lede" path="contact.lede" value={content.contact.lede} multiline />;
}

// ─── site settings (edit mode only) ───────────────────────────
// Exposes the two operational settings an editor may change: the Calendly
// booking link and the list of emails that receive contact-form inquiries.
// Both stage like any other edit and publish on Save.
function SettingsEditor() {
  const { editing } = useAuth();
  const { content, setField } = useContent();
  if (!editing) return null;
  const recipients = (content.settings.contactRecipients || []).join(', ');
  return (
    <section id="site-settings" className="settings-editor">
      <div className="container-narrow">
        <div className="section__head">
          <span className="section__rule" aria-hidden="true"></span>
          <h2>Site settings</h2>
          <p className="lede">Only visible in edit mode. Changes publish on Save.</p>
        </div>
        <div className="settings-editor__grid">
          <label className="settings-editor__field">
            <span>Calendly booking URL</span>
            <input
              type="url"
              defaultValue={content.settings.calendlyUrl}
              placeholder="https://calendly.com/your-handle/cello-lesson"
              onBlur={e => setField('settings.calendlyUrl', e.target.value.trim())}
            />
            <small>Blank → falls back to the built-in link. Controls the Schedule page embed.</small>
          </label>
          <label className="settings-editor__field">
            <span>Contact inquiry recipients</span>
            <input
              type="text"
              defaultValue={recipients}
              placeholder="berti@example.com, studio@example.com"
              onBlur={e => setField(
                'settings.contactRecipients',
                e.target.value.split(',').map(s => s.trim()).filter(Boolean)
              )}
            />
            <small>Comma-separated. Who receives contact-form messages. Blank → the built-in list.</small>
          </label>
        </div>
      </div>
    </section>
  );
}

// ─── hero ─────────────────────────────────────────────────────
function Hero() {
  const { content } = useContent();
  const { editing } = useAuth();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header className="hero" id="top">
      <div className="hero__watermark" aria-hidden="true"><Mark size={560} /></div>
      <div className="hero__kicker kicker">evenstarmusic.com</div>
      <h1>Berti</h1>
      {editing ? (
        <EditableText as="div" className="hero__sub" path="hero.sub" value={content.hero.sub} />
      ) : (
        <div className="hero__sub">
          {content.hero.sub.split(' · ').map((part, i, arr) => (
            <React.Fragment key={i}>
              <span className="hero__sub-part">{part.trim()}</span>
              {i < arr.length - 1 && <span className="hero__sub-sep"> · </span>}
            </React.Fragment>
          ))}
        </div>
      )}
      <EditableText as="p" className="hero__tagline" path="hero.tagline" value={content.hero.tagline} multiline />
      <div className="hero__ctas">
        <Link to="/schedule" className="btn btn--primary">Schedule a Lesson</Link>
        <a href="#contact" className="btn">Contact Me</a>
      </div>
      <div className={`hero__scroll ${scrolled ? 'hero__scroll--hidden' : ''}`} aria-hidden={scrolled}>
        <span>scroll</span>
        <span className="hero__scroll-line" />
      </div>
    </header>
  );
}

// ─── about ────────────────────────────────────────────────────
function About() {
  const { content } = useContent();
  return (
    <section id="about">
      <div className="container">
        <Reveal>
          <div className="section__head">
            <span className="section__rule" aria-hidden="true"></span>
            <h2>About</h2>
            <EditableText as="p" className="lede" path="about.lede" value={content.about.lede} multiline />
          </div>
        </Reveal>

        <Reveal>
          <div className="about__grid">
            <div className="about__photo">
              <EditableImage path="about.headshot" src={content.about.headshot} alt="Berti, cellist and teacher" />
            </div>
            <div className="about__body">
              <EditableText as="p" path="about.bio1" value={content.about.bio1} multiline />
              <EditableText as="blockquote" className="about__pullquote" path="about.pullquote" value={content.about.pullquote} multiline />
              <EditableText as="p" path="about.bio2" value={content.about.bio2} multiline />
              <EditableText as="p" path="about.bio3" value={content.about.bio3} multiline />
              <p style={{ marginTop: 24 }}>
                <a href="/cv.pdf" className="kicker" style={{ paddingBottom: 2 }}>
                  Download full CV (PDF) →
                </a>
              </p>

              <figure className="about__moment">
                <EditableImage path="about.moment" src={content.about.moment} alt="Berti with her cello outside Symphony Hall, Phoenix" />
                <figcaption>
                  <span className="about__moment-title">Symphony Hall, Phoenix</span>
                  <span className="about__moment-meta">Phoenix Symphony Guild · 2010</span>
                </figcaption>
              </figure>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

// ─── mentors ──────────────────────────────────────────────────
function Mentors() {
  const { content } = useContent();
  return (
    <section id="mentors">
      <div className="container">
        <Reveal>
          <div className="section__head">
            <span className="section__rule" aria-hidden="true"></span>
            <h2>My Mentors</h2>
            <EditableText as="p" className="lede" path="mentors.lede" value={content.mentors.lede} multiline />
          </div>
        </Reveal>

        <Reveal>
          <div className="mentors">
            {content.mentors.list.map((m, i) => (
              <figure className="mentor-card" key={i}>
                <div className="mentor-card__photo">
                  <EditableImage path={`mentors.list.${i}.photo`} src={m.photo} alt={`${m.name}, ${m.role}`} />
                </div>
                <figcaption>
                  <EditableText as="div" className="mentor-card__name" path={`mentors.list.${i}.name`} value={m.name} />
                  <EditableText as="div" className="mentor-card__role kicker" path={`mentors.list.${i}.role`} value={m.role} />
                  <EditableText as="p" className="mentor-card__note" path={`mentors.list.${i}.note`} value={m.note} multiline />
                </figcaption>
              </figure>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

// ─── teaching ─────────────────────────────────────────────────
// Fixed decorative icons, paired by index with the editable teaching cards.
const TEACHING_ICONS = [
  <svg className="pillar__icon" viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth={1.2} aria-hidden="true">
    <circle cx="24" cy="14" r="3" />
    <path d="M24 17 L14 38" />
    <path d="M24 17 L34 38" />
    <path d="M18 30 L30 30" />
  </svg>,
  <svg className="pillar__icon" viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth={1.2} aria-hidden="true">
    <path d="M14 24 Q14 14 24 14 Q34 14 34 24" />
    <path d="M20 24 Q20 18 24 18 Q28 18 28 24" />
    <path d="M14 28 L14 32 M18 26 L18 34 M22 24 L22 36 M26 26 L26 34 M30 28 L30 32 M34 30 L34 30" />
  </svg>,
  <svg className="pillar__icon" viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth={1.2} aria-hidden="true">
    <path d="M24 8 Q14 18 14 28 Q14 38 24 40 Q34 38 34 28 Q34 18 24 8 Z" />
    <path d="M24 14 L24 40" />
    <path d="M24 24 Q20 22 18 24 M24 24 Q28 22 30 24" />
  </svg>,
];

function Teaching() {
  const { content } = useContent();
  return (
    <section id="teaching">
      <div className="container">
        <Reveal>
          <div className="section__head">
            <span className="section__rule" aria-hidden="true"></span>
            <h2>Teaching</h2>
            <EditableText as="p" className="lede" path="teaching.lede" value={content.teaching.lede} multiline />
          </div>
        </Reveal>

        <Reveal>
          <div className="pillars">
            {content.teaching.cards.map((card, i) => (
              <div className="pillar" key={i}>
                {TEACHING_ICONS[i] ?? TEACHING_ICONS[0]}
                <EditableText as="h2" className="pillar__title" path={`teaching.cards.${i}.title`} value={card.title} />
                <EditableText as="p" path={`teaching.cards.${i}.body`} value={card.body} multiline />
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal>
          <div className="teaching__essay">
            <EditableText as="p" path="teaching.essay1" value={content.teaching.essay1} multiline />
            <EditableText as="p" path="teaching.essay2" value={content.teaching.essay2} multiline />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

// ─── performance ──────────────────────────────────────────────
function Performance() {
  const { content } = useContent();
  const { editing } = useAuth();
  const [showAll, setShowAll] = useState(false);
  const VISIBLE = 3;   // shown initially
  const MAX = 9;       // shown after "Show more"
  const shown = content.performances.slice(0, showAll ? MAX : VISIBLE);
  return (
    <section id="performance">
      <div className="container">
        <Reveal>
          <div className="section__head">
            <span className="section__rule" aria-hidden="true"></span>
            <h2>Performance</h2>
            <p className="lede">
              Selected recordings from the cellists I learn from. {/* TODO: swap for Berti's own footage when ready. */}
            </p>
          </div>
        </Reveal>

        {editing ? (
          <PerformanceEditor />
        ) : (
          <Reveal>
            <div className="perf-grid">
              {shown.map(p => (
                <article key={p.id} className="perf-card">
                  <div className="perf-card__embed">
                    <iframe
                      src={`https://www.youtube-nocookie.com/embed/${p.videoId}?rel=0`}
                      title={p.title}
                      loading="lazy"
                      allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                    />
                  </div>
                  <div className="perf-card__body">
                    <div className="perf-card__title">{p.title}</div>
                    <div className="perf-card__meta">{p.meta}</div>
                  </div>
                </article>
              ))}
            </div>
            {content.performances.length > VISIBLE && (
              <div className="perf-viewmore">
                <button type="button" className="btn" onClick={() => setShowAll(v => !v)}>
                  {showAll ? 'Show fewer' : `Show more (${Math.min(MAX, content.performances.length) - VISIBLE})`}
                </button>
              </div>
            )}
          </Reveal>
        )}

        <Reveal>
          <div className="perf-archive-link">
            <Link to="/portfolio" className="btn">View Full Archive</Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

// ─── listening ────────────────────────────────────────────────
function Listening() {
  const { content } = useContent();
  return (
    <section id="listening">
      <div className="container">
        <Reveal>
          <div className="section__head">
            <span className="section__rule" aria-hidden="true"></span>
            <h2>What I'm Listening To</h2>
            <EditableText as="p" className="lede" path="listening.lede" value={content.listening.lede} multiline />
          </div>
        </Reveal>

        <Reveal>
          <div className="listen-grid">
            {content.playlists.map(p => (
              <a
                key={p.id}
                href={p.url}
                target="_blank"
                rel="noopener noreferrer"
                className={`listen-card listen-card--${p.tint}`}
              >
                <div className="listen-card__art">
                  {p.image ? (
                    <img className="listen-card__thumb" src={p.image} alt="" loading="lazy" />
                  ) : (
                    <svg viewBox="0 0 60 60" aria-hidden="true">
                      <circle cx="30" cy="30" r="28" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.4" />
                      <circle cx="30" cy="30" r="20" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.4" />
                      <circle cx="30" cy="30" r="12" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.4" />
                      <circle cx="30" cy="30" r="3" fill="currentColor" />
                    </svg>
                  )}
                </div>
                <div className="listen-card__body">
                  <div className="listen-card__title">{p.title}</div>
                  <p className="listen-card__desc">{p.desc}</p>
                  <div className="listen-card__cta">
                    <svg className="spotify-logo" viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.5 17.3c-.22.36-.68.48-1.03.26-2.8-1.71-6.33-2.1-10.48-1.15-.42.1-.82-.16-.92-.56-.1-.42.16-.82.57-.92 4.55-1.04 8.44-.58 11.58 1.34.36.22.48.68.28 1.03zm1.47-3.28c-.28.44-.86.6-1.3.32-3.2-1.97-8.09-2.54-11.88-1.39-.5.15-1.03-.13-1.18-.62-.15-.5.13-1.03.62-1.18 4.33-1.31 9.72-.67 13.4 1.59.42.26.56.84.34 1.28zm.13-3.41C15.25 8.44 8.87 8.24 5.15 9.37c-.6.18-1.24-.16-1.42-.76-.18-.6.16-1.23.76-1.42 4.28-1.3 11.33-1.05 15.83 1.62.54.32.72 1.02.4 1.55-.32.54-1.02.72-1.56.4z" />
                    </svg>
                    <span>Listen on Spotify ↗</span>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

// ─── lessons ──────────────────────────────────────────────────
function Lessons() {
  const { content } = useContent();
  return (
    <section id="lessons">
      <div className="container">
        <Reveal>
          <div className="section__head">
            <span className="section__rule" aria-hidden="true"></span>
            <h2>Lessons</h2>
            <EditableText as="p" className="lede" path="lessons.lede" value={content.lessons.lede} multiline />
          </div>
        </Reveal>

        <Reveal>
          <div className="lessons__grid">
            <div className="lessons__intro">
              <h3>What to expect</h3>
              <EditableText as="p" path="lessons.expect" value={content.lessons.expect} multiline />

              <h3>Who I teach</h3>
              <ul className="lessons__list">
                <li><span>Children (age 7+)</span><span className="kicker">Beginners welcome</span></li>
                <li><span>Teenagers</span><span className="kicker">Pre-college prep available</span></li>
                <li><span>Adult learners</span><span className="kicker">Returning + new</span></li>
                <li><span>Advanced &amp; conservatory-track</span><span className="kicker">By interview</span></li>
              </ul>

              <h3>Location</h3>
              <EditableText as="p" path="lessons.location" value={content.lessons.location} multiline />
            </div>

            <aside className="rate-card">
              <h3>Lesson rates</h3>
              <ul>
                {content.lessons.rates.map((r, i) => (
                  <li key={i}>
                    <EditableText as="span" className="rate-label" path={`lessons.rates.${i}.label`} value={r.label} />
                    <EditableText as="span" className="rate-price" path={`lessons.rates.${i}.price`} value={r.price} />
                  </li>
                ))}
                {/* Animal Crackers (Marx Bros, 1930): "I'd horsewhip you if I had a horse." */}
                <li
                  className="rate-card__joke"
                  title="Animal Crackers (1930). Captain Spaulding's billing card."
                >
                  <span className="rate-label">Not Rehearsing</span>
                  <span className="rate-price">∞</span>
                </li>
              </ul>
              <Link to="/schedule" className="btn btn--primary" style={{ width: '100%', justifyContent: 'center' }}>
                Schedule a Lesson
              </Link>
              <p style={{ marginTop: 16, textAlign: 'center' }}>
                <Link to="/lesson-policies" className="kicker">Lesson policies →</Link>
              </p>
              <p style={{ marginTop: 8, textAlign: 'center' }}>
                <Link to="/pay" className="kicker">Pay for a lesson →</Link>
              </p>
            </aside>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

// ─── contact ──────────────────────────────────────────────────
type SendStatus = 'idle' | 'sending' | 'ok' | 'error';

function Contact() {
  const [status, setStatus] = useState<SendStatus>('idle');

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;

    // No delivery key configured yet → fall back to the visitor's mail
    // client so the form is never a dead end.
    if (!CONTACT_FORM_READY) {
      window.location.href = CONTACT_MAILTO;
      return;
    }

    setStatus('sending');
    try {
      const data = new FormData(form);
      const payload = {
        name: data.get('name'),
        email: data.get('email'),
        inquiry_type: data.get('inquiry_type'),
        student_age: data.get('student_age'),
        message: data.get('message'),
        botcheck: data.get('botcheck'),
      };
      const res = await fetch(CONTACT_FORM_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        setStatus('ok');
        form.reset();
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  }

  return (
    <section id="contact" className="contact">
      <div className="container-narrow">
        <Reveal>
          <div className="section__head">
            <span className="section__rule" aria-hidden="true"></span>
            <h2>Contact</h2>
            <ContactLede />
          </div>
        </Reveal>

        <Reveal>
          {status === 'ok' ? (
            <div className="contact__thanks" role="status">
              <svg width="20" height="20" viewBox="0 0 14 14" fill="currentColor" aria-hidden="true">
                <path d="M7 0 L8 6 L14 7 L8 8 L7 14 L6 8 L0 7 L6 6 Z" />
              </svg>
              <p>
                Thank you — your message is on its way. Bertis will reply within a
                few days at the email you gave.
              </p>
            </div>
          ) : (
            <form className="contact__form" onSubmit={handleSubmit}>
              {/* Honeypot — hidden from humans, catches bots */}
              <input type="checkbox" name="botcheck" tabIndex={-1} autoComplete="off" style={{ display: 'none' }} />

              <div className="field">
                <label htmlFor="cf-name">Name</label>
                <input id="cf-name" name="name" type="text" required />
              </div>
              <div className="field">
                <label htmlFor="cf-email">Email</label>
                <input id="cf-email" name="email" type="email" required />
              </div>
              <div className="field">
                <label htmlFor="cf-subject">Inquiry</label>
                <select id="cf-subject" name="inquiry_type" defaultValue="Lesson inquiry">
                  <option>Lesson inquiry</option>
                  <option>Performance booking</option>
                  <option>Something else</option>
                </select>
              </div>
              <div className="field">
                <label htmlFor="cf-age">
                  Student age &amp; experience{' '}
                  <span style={{ textTransform: 'none', letterSpacing: 0 }}>(if applicable)</span>
                </label>
                <input id="cf-age" name="student_age" type="text" placeholder="e.g. 9 years old, two years of cello" />
              </div>
              <div className="field">
                <label htmlFor="cf-msg">Message</label>
                <textarea id="cf-msg" name="message" required />
              </div>
              <button
                type="submit"
                className="btn btn--primary"
                style={{ justifySelf: 'start' }}
                disabled={status === 'sending'}
              >
                {status === 'sending' ? 'Sending…' : 'Send'}
              </button>
              {status === 'error' && (
                <p className="contact__error" role="alert">
                  Something went wrong sending that. Please email{' '}
                  <a href={CONTACT_MAILTO}>{CONTACT_EMAIL}</a> directly.
                </p>
              )}
            </form>
          )}
        </Reveal>

        <div className="contact__alt">
          <p>
            Or write directly: <a href={CONTACT_MAILTO}>{CONTACT_EMAIL}</a>
          </p>
        </div>
      </div>
    </section>
  );
}
